/**
 * Raw IndexedDB access. No wrapper library.
 *
 * Rules that are not optional in a multi-tab app:
 *  - onversionchange closes the connection, or two open tabs deadlock forever
 *    after a schema bump.
 *  - Every operation opens a FRESH transaction and awaits nothing but IndexedDB
 *    requests. Transactions auto-commit when the microtask queue yields, so an
 *    awaited non-IDB promise inside one produces intermittent
 *    TransactionInactiveError.
 */

import type { Board } from '../scene/types';

export const DB_NAME = 'slate-whiteboard';
/** Governs store structure only. Document shape has its own docVersion. */
export const IDB_VERSION = 1;
export const BOARDS_STORE = 'boards';
export const META_STORE = 'meta';

const PROBE_TIMEOUT_MS = 3000;

export interface BoardSummary {
  readonly id: string;
  readonly name: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly isSample: boolean;
  readonly elementCount: number;
}

export interface SceneStore {
  readonly mode: 'indexeddb' | 'memory';
  listBoards(): Promise<BoardSummary[]>;
  loadBoard(id: string): Promise<Board | null>;
  loadAllBoards(): Promise<Board[]>;
  saveBoard(board: Board): Promise<void>;
  deleteBoard(id: string): Promise<void>;
  getMeta<T>(key: string): Promise<T | null>;
  setMeta(key: string, value: unknown): Promise<void>;
}

export class StorageUnavailableError extends Error {
  constructor(
    message: string,
    readonly reason: string,
  ) {
    super(message);
    this.name = 'StorageUnavailableError';
  }
}

export function isQuotaError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: unknown }).name;
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED';
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/** Resolves once the transaction commits, and surfaces a quota abort as an error. */
function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

function summarize(board: Board): BoardSummary {
  return {
    id: board.id,
    name: board.name,
    createdAt: board.createdAt,
    updatedAt: board.updatedAt,
    isSample: board.isSample,
    elementCount: board.elements.filter((el) => !el.isDeleted).length,
  };
}

export function openDatabase(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new StorageUnavailableError('IndexedDB is not available.', 'unsupported'));
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, IDB_VERSION);
    } catch (error) {
      reject(
        new StorageUnavailableError(
          'The browser refused to open local storage.',
          error instanceof Error ? error.name : 'blocked',
        ),
      );
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(BOARDS_STORE)) {
        const store = db.createObjectStore(BOARDS_STORE, { keyPath: 'id' });
        store.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };
    request.onblocked = () => {
      reject(
        new StorageUnavailableError(
          'Another tab is holding an older version of the database open. Close it and reload.',
          'blocked',
        ),
      );
    };
    request.onerror = () =>
      reject(
        new StorageUnavailableError(
          'Local storage could not be opened. Private browsing often blocks it.',
          request.error?.name ?? 'error',
        ),
      );
    request.onsuccess = () => {
      const db = request.result;
      // Without this, a second tab upgrading the schema blocks forever.
      db.onversionchange = () => db.close();
      resolve(db);
    };
  });
}

function createIdbStore(db: IDBDatabase): SceneStore {
  const read = <T>(storeName: string, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const tx = db.transaction(storeName, 'readonly');
    return promisify(run(tx.objectStore(storeName)));
  };

  const write = async (
    storeName: string,
    run: (store: IDBObjectStore) => void,
  ): Promise<void> => {
    const tx = db.transaction(storeName, 'readwrite');
    const done = transactionDone(tx);
    run(tx.objectStore(storeName));
    await done;
  };

  return {
    mode: 'indexeddb',
    async listBoards() {
      const all = await read<Board[]>(BOARDS_STORE, (s) => s.getAll() as IDBRequest<Board[]>);
      return all.map(summarize).sort((a, b) => b.updatedAt - a.updatedAt);
    },
    async loadBoard(id) {
      const board = await read<Board | undefined>(
        BOARDS_STORE,
        (s) => s.get(id) as IDBRequest<Board | undefined>,
      );
      return board ?? null;
    },
    async loadAllBoards() {
      const all = await read<Board[]>(BOARDS_STORE, (s) => s.getAll() as IDBRequest<Board[]>);
      return all.sort((a, b) => b.updatedAt - a.updatedAt);
    },
    async saveBoard(board) {
      // Handed over as a plain object: structured clone is faster than
      // JSON.stringify and keeps numbers as numbers.
      await write(BOARDS_STORE, (s) => void s.put(board));
    },
    async deleteBoard(id) {
      await write(BOARDS_STORE, (s) => void s.delete(id));
    },
    async getMeta<T>(key: string) {
      const row = await read<{ key: string; value: T } | undefined>(
        META_STORE,
        (s) => s.get(key) as IDBRequest<{ key: string; value: T } | undefined>,
      );
      return row ? row.value : null;
    },
    async setMeta(key, value) {
      await write(META_STORE, (s) => void s.put({ key, value }));
    },
  };
}

export function createMemoryStore(): SceneStore {
  const boards = new Map<string, Board>();
  const meta = new Map<string, unknown>();
  return {
    mode: 'memory',
    async listBoards() {
      return [...boards.values()].map(summarize).sort((a, b) => b.updatedAt - a.updatedAt);
    },
    async loadBoard(id) {
      return boards.get(id) ?? null;
    },
    async loadAllBoards() {
      return [...boards.values()].sort((a, b) => b.updatedAt - a.updatedAt);
    },
    async saveBoard(board) {
      boards.set(board.id, board);
    },
    async deleteBoard(id) {
      boards.delete(id);
    },
    async getMeta<T>(key: string) {
      return (meta.get(key) as T | undefined) ?? null;
    },
    async setMeta(key, value) {
      meta.set(key, value);
    },
  };
}

export interface StoreResult {
  readonly store: SceneStore;
  /** Set when storage failed and the in-memory fallback is in use. */
  readonly degradedReason: string | null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new StorageUnavailableError(`${label} timed out.`, 'timeout')),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Open storage and prove it works with a real write-then-read round trip.
 * An open() that succeeds but whose writes fail is a real private-mode case, so
 * "it opened" is not enough evidence.
 */
export async function createStore(): Promise<StoreResult> {
  try {
    const db = await withTimeout(openDatabase(), PROBE_TIMEOUT_MS, 'Opening local storage');
    const store = createIdbStore(db);
    const probeValue = Date.now();
    await withTimeout(store.setMeta('__probe', probeValue), PROBE_TIMEOUT_MS, 'Storage probe');
    const readBack = await withTimeout(
      store.getMeta<number>('__probe'),
      PROBE_TIMEOUT_MS,
      'Storage probe',
    );
    if (readBack !== probeValue) {
      throw new StorageUnavailableError('Local storage did not retain a test value.', 'unreliable');
    }
    return { store, degradedReason: null };
  } catch (error) {
    const reason =
      error instanceof StorageUnavailableError
        ? error.message
        : 'Local storage could not be used in this browser session.';
    return { store: createMemoryStore(), degradedReason: reason };
  }
}
